import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
  useTheme,
  IconButton,
  TablePagination,
  Button,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";

import {
  Edit,
  Delete,
  AddCircleRounded,
  Password
} from "@mui/icons-material";
import { useState, useEffect, useRef } from "react";
import { useUserContext, User } from "../../context/UserContext";
import { BarragemData, useBarragemContext } from "../../context";
import { ConfirmModal, ErrorOnDialog, LoadingModal } from "../modals";
import { Paginated, RequestError, useApi } from "../../services";
import { FuncionarioRegister } from "./funcionariosRegister";
import { formatBrazilianPhone, useWindowSize } from "../../core";

export interface FuncionariosTableProps {
  title?: string;
  isRegister?: boolean;
  isFilter?: boolean;
  isSendPassword?: boolean;
}

export const FuncionariosTable: React.FC<FuncionariosTableProps> = ({
  title,
  isRegister,
  isFilter,
  isSendPassword,
}) => {
  const api = useApi();
  const theme = useTheme();
  const user = useUserContext();
  const isAdmin = (user as User)?.admin ?? false;
  const { height, width } = useWindowSize();
  const { idEmpreendimento } = useBarragemContext().barragem as BarragemData;

  const divRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const [state, setState] = useState(false);

  const [funcionarios, setFuncionarios] = useState<Paginated<User>>({
    items: [],
    page: 0,
    perPage: 50,
    totalPages: 10,
    totalItems: 1000,
  });
  const [filterField, setFilterField] = useState<keyof User>("nome");
  const [filterValue, setFilterValue] = useState("");


  const [loading, setLoading] = useState(true);
  const [snackbarData, setSnackbarData] = useState<{ tipo: "error" | "info" | "success" | "warning"; message: string; }>();
  const [error, setError] = useState<RequestError>();

  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<User>();

  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {

    setLoading(true);
    api.user.getFuncionariosByCompany(idEmpreendimento, funcionarios.page + 1, funcionarios.perPage).then((value) => {
      if ("error" in value) {
        setError(value);
        setLoading(false);
        return;
      }
      setFuncionarios({
        ...value,
        page: value.page - 1,
      });
      setLoading(false);
    });
  }, [idEmpreendimento, state]);


  // Tamanho da tabela
  useEffect(() => {
    setTimeout(() => {
      if (!divRef.current) return;

      const { width } = divRef.current.getBoundingClientRect();
      let { height } = divRef.current.getBoundingClientRect();

      console.log(height);

      if (titleRef.current) {
        const size = titleRef.current.getBoundingClientRect();
        height -= size.height;
      }
      if (paginationRef.current) {
        const size = paginationRef.current.getBoundingClientRect();
        height -= size.height;
      }
      if (optionsRef.current) {
        const size = optionsRef.current.getBoundingClientRect();
        height -= size.height;
      }

      console.log(height);

      setSize({ width, height });
    }, 100);
  }, [divRef, titleRef, paginationRef, optionsRef, height, width]);

  const handleDelete = (idFuncionario: string) => {
    setSelectedId(idFuncionario);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;

    api.user.remove(selectedId).then((value) => {
      if (value === true) {
        setState(!state);
        return;
      }

      setError(value);
      return;
    });

    setShowConfirm(false);
    setSelectedId(null);
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setSelectedId(null);
  };


  const sendPassword = (funcionario: User) => {
    setLoading(true);
    api.user.sendPassword(funcionario.id).then(value => {
      if ('error' in value) {
        setError(error);
        setLoading(false);
        return;
      }

      setSnackbarData({
        tipo: "success",
        message: `Senha enviada para ${value.email}`,
      });
      setLoading(false);
    });
  };


  const handleFilterFieldChange = (event: SelectChangeEvent<keyof User>) => {
    setFilterField(event.target.value as keyof User);
  };

  const handleFilterValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(event.target.value);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setFuncionarios({
      ...funcionarios,
      page: newPage,
    });
    setState(!state);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFuncionarios({
      ...funcionarios,
      page: 0,
      perPage: parseInt(event.target.value, 10),
    });
    setState(!state);
  };

  const handleAddSuccess = () => {
    setState(!state);
  };

  const handleUpdateSuccess = () => {
    setState(!state);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  if (loading) {
    return <LoadingModal open={true} message="Carregando funcionários..." />;
  }

  return (<>
    {/* Erro */}
    {error && <ErrorOnDialog
      open={true}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    {/* Confirm Delete */}
    <ConfirmModal
      open={showConfirm}
      title="Confirmar exclusão"
      message="Tem certeza que deseja remover este funcionário?"
      onConfirm={confirmDelete}
      onCancel={cancelDelete}
    />

    {/* Mensagem */}
    <Snackbar
      open={Boolean(snackbarData)}
      autoHideDuration={3000}
      onClose={() => setSnackbarData(undefined)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarData(undefined)} severity={snackbarData?.tipo ?? 'success'} variant="filled" sx={{ width: '100%' }}>
        {snackbarData?.message}
      </Alert>
    </Snackbar>

    {/* Novo funcionário */}
    {openDialog && <FuncionarioRegister
      open={openDialog}
      onClose={handleCloseDialog}
      onAdd={handleAddSuccess}
      userEmpreendimento={idEmpreendimento}
    />}

    {/* Editar funcionário */}
    {editUser && <FuncionarioRegister
      open={Boolean(editUser)}
      initialFuncionario={editUser}
      onClose={() => setEditUser(undefined)}
      onUpdate={handleUpdateSuccess}
      userEmpreendimento={editUser.empreendimento}
    />}

    {/* Tabela */}
    <Box
      ref={divRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        paddingTop: "15px",
        marginBottom: "15px",
        paddingBottom: "15px",
        flexDirection: "column",
      }}
    >

      {/* Título */}
      {title &&
        <Box ref={titleRef}>
          <Typography fontWeight="bold" fontSize="1.8em" sx={{ paddingLeft: "2em" }}>
            {title}
          </Typography>

          <div
            style={{
              margin: "15px",
              height: "0.2em",
              marginBottom: "21px",
              backgroundColor: theme.palette.primary.main,
            }}
          />
        </Box>
      }

      {/* Filtros e novo Funcionário */}
      {(isFilter || isRegister) &&
        <Box ref={optionsRef} sx={{ display: "flex", alignItems: "center", px: 2, mb: 2 }}>
          {isFilter &&
            <>
              <Select
                value={filterField}
                onChange={handleFilterFieldChange}
                variant="outlined"
                size="small"
                sx={{ mr: 1, minWidth: 120 }}
              >
                <MenuItem value="nome">Nome</MenuItem>
                <MenuItem value="setor">Setor</MenuItem>
                <MenuItem value="funcao">Função</MenuItem>
              </Select>
              <TextField
                value={filterValue}
                onChange={handleFilterValueChange}
                variant="outlined"
                size="small"
                placeholder="Filtrar..."
              />
            </>
          }

          <div style={{ flex: 1 }} />

          {isAdmin && isRegister && (
            <Button
              variant="contained"
              startIcon={<AddCircleRounded />}
              onClick={handleOpenDialog}
            >
              Cadastrar funcionário
            </Button>
          )}
        </Box>
      }

      {/* Tabela */}
      <TableContainer sx={{ flex: 1, width: "100%", height: size.height }}>
        <Table stickyHeader size='small' sx={{ overflow: "auto" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Nome</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Funções</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Telefones</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>E-mail</TableCell>
              {isAdmin && isSendPassword && <TableCell align="center" sx={{ fontWeight: "bold" }}>Enviar Senha</TableCell>}
              {isAdmin && <TableCell align="center" sx={{ fontWeight: "bold" }}>Ações</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {funcionarios.items.map((funcionario) => (
              <TableRow key={funcionario.id}>
                <TableCell align="center">{funcionario.nome}</TableCell>
                <TableCell align="center">{(() => {
                  const barragens = new Set(funcionario.funcoes.map(func => func.barragem));
                  return `${funcionario.funcoes.length} ${funcionario.funcoes.length > 1 ? "funções" : "função"} em ${barragens.size} barrage${barragens.size > 1 ? "ns" : "m"}`;
                })()}</TableCell>
                <TableCell align="center">{formatBrazilianPhone(funcionario.telefones ?? "")}</TableCell>
                <TableCell align="center">{funcionario.email}</TableCell>
                {isAdmin && isSendPassword && (
                  <TableCell align="center">
                    <Tooltip title="Enviar Senha para o Funcionário">
                      <IconButton onClick={() => sendPassword(funcionario)}>
                        <Password />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
                {isAdmin && (
                  <TableCell align="center">
                    <>
                      <IconButton onClick={() => setEditUser(funcionario)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(funcionario.id)}>
                        <Delete />
                      </IconButton>
                    </>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <TablePagination
        ref={paginationRef}
        component="div"
        page={funcionarios.page}
        count={funcionarios.totalItems}
        onPageChange={handleChangePage}
        rowsPerPage={funcionarios.perPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    </Box >
  </>
  );
};